<script lang="ts">
  import { slide } from "svelte/transition";
  import { uniqueID } from "../../util";
  import { RadioGroup, RadioItem, CustomSelect } from "../index";

  export let optionsLayout: "inline" | "above" = "inline";

  interface SearchOption {
    key: string;
    title: string;
    placholder: string;
    searchBaseUrl: string;
    helpText: string;
    suggestionsBaseUrl?: string | undefined;
    suggestionsDirectBaseUrl?: string | undefined;
    directoryModeExclusion?: boolean | undefined; // should this option be deleted in directory mode?
  }

  const searchOptions: SearchOption[] = [
    {
      key: "site",
      title: "Site Search",
      placholder: "Search med.uc.edu",
      helpText: "Search all of med.uc.edu",
      searchBaseUrl:
        "/search-results?indexCatalogue=med-2023&wordsMode=AllWords&searchQuery=",
      directoryModeExclusion: true,
    },
    {
      key: "lastname",
      title: "Last Name",
      placholder: "Search by Last Name",
      helpText: "Find an expert by their last name",
      searchBaseUrl: "/landing-pages/profile/IndexSearch/Alpha/",
      suggestionsBaseUrl: "/IndexJSNames/",
      suggestionsDirectBaseUrl: "/landing-pages/profile/Index/Pubs/",
    },
    {
      key: "keyword",
      title: "Keyword",
      placholder: "Search by Keyword",
      helpText: "Search College of Medicine experts by keyword",
      searchBaseUrl: "/landing-pages/profile/IndexSearch/Keyword/",
    },
    {
      key: "specialty",
      title: "Specialty",
      placholder: "Search by Specialty",
      helpText: "Search College of Medicine experts by specialty",
      searchBaseUrl: "/landing-pages/profile/IndexSearch/Specialty/",
      suggestionsBaseUrl: "/IndexJSSpecialty/",
      suggestionsDirectBaseUrl: "/landing-pages/profile/IndexSearch/Specialty/",
    },
  ];

  $: directoryModeOptions = searchOptions.filter(
    (item) => !item.directoryModeExclusion
  )

  const uid = uniqueID();
  let searchTerm: string;
  let searchButtonDOM: any;
  let value: string = "site";
  let suggestions: any[] = [];
  let suggestionSelectionIndex: number = -1;
  let suggestionsDOM: any;

  $: activeChoice = searchOptions.find((_) => _.key === value);
  $: if (value) {
    clearSuggestions();
    searchTerm = "";
  }

  const clearSuggestions = () => {
    suggestions = [];
    suggestionSelectionIndex = -1;
  };

  const suggestionsFetch = async () => {
    const req = await fetch(`${activeChoice?.suggestionsBaseUrl}${searchTerm}`);
    const res = await req.json();

    if (res.length > 0) {
      suggestions = res;
    } else {
      suggestions = [];
    }
  };

  const nameOptionClickHandler = async (e: any) => {
    window.location.href = `${activeChoice?.suggestionsDirectBaseUrl}${e.target.dataset.ucname}`;
  };

  const handleTextboxKeyup = async (e: any) => {
    switch (e.keyCode) {
      case 38: // up arrow
        e.preventDefault();

        if (suggestions.length > 0) {
          if (suggestionSelectionIndex < 1) {
            suggestionSelectionIndex = suggestionsDOM.children.length - 1;
          } else {
            suggestionSelectionIndex = suggestionSelectionIndex - 1;
          }

          // move to the new index
          suggestionsDOM.children[suggestionSelectionIndex].selected = true;
        }
        break;
      case 40: // down arrow
        e.preventDefault();

        if (suggestions.length > 0) {
          if (suggestionSelectionIndex >= suggestionsDOM.children.length - 1) {
            // go to the beginning
            suggestionSelectionIndex = 0;
          } else {
            // increment (visually, go 'down one')
            suggestionSelectionIndex = suggestionSelectionIndex + 1;
          }

          // move to the new index
          suggestionsDOM.children[suggestionSelectionIndex].selected = true;
        }
        break;
      case 13: // user pressed Enter from the context of the search box – if autocomplete is visible and something is selected, click it.
        e.preventDefault() && e.stopPropagation();

        if (suggestions.length > 0) {
          const selected = suggestionsDOM.children[suggestionSelectionIndex];
          if (selected) {
            selected.click();
          } else {
            searchButtonDOM.click();
          }
        }
        break;
      default: // assume it's a normal character and proceed
        if (searchTerm.length >= 3) {
          if (activeChoice?.suggestionsBaseUrl) {
            await suggestionsFetch();
          } else {
            // handle emptying and disabling things
            clearSuggestions();
          }
        } else {
          clearSuggestions();
        }
    }
  };

  let classes: string = "";
  export { classes as class };
  export let mobileMenuPlacement = false;
  export let directoryMode = false;
</script>

<div
  class="com-search {classes}"
  class:mobile-menu-placement={mobileMenuPlacement}
  class:options-inline={optionsLayout === "inline"}
  class:options-above={optionsLayout === "above"}
  class:directory-mode={directoryMode}
>
  {#if optionsLayout === "above"}
    <div class="options-wrapper">
      <RadioGroup
        labelledby="radio-{uid}"
        class="search-choice mx-auto bg-bearcat-black"
      >
        {#each searchOptions as _}
          <RadioItem
            bind:group={value}
            name={_.key}
            value={_.key}
            title={_.helpText}>{_.title}</RadioItem
          >
        {/each}
      </RadioGroup>
    </div>
  {/if}
  <div class="input-wrapper">
    {#if optionsLayout === "inline"}
      <div class="inline-search-options grow">
        <CustomSelect
          on:NEW_SEARCH_KEY={(e) => {
            value = e.detail;
          }}
          options={directoryMode ? directoryModeOptions : searchOptions}
        />
      </div>
    {/if}
    <div class="input-shrinkwrap shrink">
      <input
        type="text"
        name="com-global-search"
        aria-label="Search Input Field"
        placeholder={activeChoice?.placholder}
        bind:value={searchTerm}
        on:keyup={handleTextboxKeyup}
      />
      {#if suggestions.length > 0}
        <select
          id="select-{uid}"
          aria-label="Select user profile"
          class="suggestions"
          size="5"
          transition:slide
          bind:this={suggestionsDOM}
        >
          {#each suggestions as _}
            <option
              value={_.Name}
              data-ucname={_.Username}
              on:click={nameOptionClickHandler}
            >
              {_.Name}
            </option>
          {/each}
        </select>
      {/if}
    </div>
    <button
      on:click={() =>
        (window.location.href = `${activeChoice?.searchBaseUrl}${searchTerm}`)}
      bind:this={searchButtonDOM}
    >
      <span class="sr-only">Search</span>
      <i class="fa-duotone fa-magnifying-glass"></i>
    </button>
  </div>
</div>

<style lang="scss" global>
  .com-search {
    --padding: 1rem 1.5rem;

    ul.options {
      margin: 0;
    }

    .options-wrapper {
      display: flex;
      color: white;

      .search-choice {
        display: flex;
        align-content: center;
        width: 100%;
      }
    }

    .input-wrapper {
      display: flex;
      text-align: center;
      font-size: 2rem;
      line-height: 2rem;

      .input-shrinkwrap {
        position: relative;
        padding: 0;
        margin: 0;
        width: 90%;

        > input {
          width: 100%;
          padding: var(--padding);
          font-size: inherit;
          line-height: inherit;
          margin: 0 auto;
          border-radius: 1rem 0 0 1rem;
          border: none;
        }

        select.suggestions {
          font-size: inherit;
          line-height: inherit;
          overflow: hidden;
          position: absolute;
          width: 100%;
          top: 100%;
          left: 0;
          z-index: 20;
          border: none;
          background-color: #000;
          color: white;
          border-radius: 0 0 2rem 2rem;

          option {
            padding: 1rem;
          }
        }
      }

      > button {
        padding: var(--padding);
        font-size: inherit;
        line-height: inherit;
        border: none;
        border-radius: 0 1rem 1rem 0;
        color: var(--uc-red);
        background-color: white;
      }
    }

    &.mobile-menu-placement {
      .options-wrapper {
        color: white;
        // font-size: 1.5rem;

        // .search-choice {
        //   font-size: 1.5rem;
        // }
      }
      .input-wrapper {
      }
    }

    &.options-inline {
      .inline-search-options {
        font-size: 1.5rem;
      }

      .input-wrapper {
        .input-shrinkwrap {
          > input {
            border-radius: 0 0 0 0;
          }
        }
      }
    }
  }
</style>
